import type { Route } from "./+types/navigation.layout";

import { Suspense } from "react";
import { Await, Outlet } from "react-router";

import { getTodaySessionUsage, isPaidUser } from "../../features/ai/queries";
import Footer from "../components/footer";
import { NavigationBar } from "../components/navigation-bar";
import { formatUsageDisplay } from "../config/app-settings";
import makeServerClient, { getSafeUser } from "../lib/supa-client.server";

export async function loader({ request }: Route.LoaderArgs) {
  const [client] = makeServerClient(request);
  const userPromise = getSafeUser(client).then(async ({ user }) => {
    if (!user) {
      return { user: null, usageDisplay: null, isTeacher: false };
    }

    // 사용량 표시 다시 활성화
    try {
      const [todayUsage, paid] = await Promise.all([
        getTodaySessionUsage(client, user.id),
        isPaidUser(client, user.id),
      ]);
      const usageDisplay = formatUsageDisplay(todayUsage.length, paid);
      // Determine teacher status: prefer registry (requires policy), fallback to enrollments
      let isTeacher = false;
      try {
        const { data: reg } = await client
          .from("teacher_registry")
          .select("id")
          .or(`teacher_user_id.eq.${user.id},teacher_email.eq.${user.email}`)
          .limit(1)
          .maybeSingle();
        if (reg) isTeacher = true;
      } catch (_) {
        // ignore
      }
      if (!isTeacher) {
        try {
          const { count } = await client
            .from("teacher_student_enrollments")
            .select("id", { count: "exact", head: true })
            .eq("teacher_user_id", user.id);
          isTeacher = (count ?? 0) > 0;
        } catch (_) {
          // ignore
        }
      }

      return { user, usageDisplay, isTeacher };
    } catch (error) {
      console.warn("Failed to load usage data:", error);
      return { user, usageDisplay: "로딩 중...", isTeacher: false };
    }
  });

  return { userPromise };
}

export default function NavigationLayout({ loaderData }: Route.ComponentProps) {
  const { userPromise } = loaderData;
  return (
    <div className="flex min-h-screen flex-col justify-between">
      <Suspense fallback={<NavigationBar loading={true} />}>
        <Await resolve={userPromise}>
          {({ user, usageDisplay, isTeacher }) =>
            user === null ? (
              <NavigationBar loading={false} />
            ) : (
              <NavigationBar
                name={user.user_metadata.name || "Anonymous"}
                email={user.email}
                avatarUrl={user.user_metadata.avatar_url}
                usageDisplay={usageDisplay || undefined}
                isTeacher={isTeacher}
                loading={false}
              />
            )
          }
        </Await>
      </Suspense>
      <div className="mx-auto my-4 w-full max-w-screen-2xl px-5 md:my-6">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
}
